/**
 * Certificate award levels based on weighted score.
 * Weighted certificate levels. Scores below 60, missing scores, or incomplete
 * scoring do not produce a certificate.
 */
export const CERTIFICATE_AWARD_LEVELS = [
    'Superior Project',
    'Outstanding Project',
    'Excellent Project',
    'Honorable Mention'
]

export const CERTIFICATE_TIER_META = {
    superior: {
        key: 'superior',
        dots: 4,
        code: 'SP',
        name: 'Superior Project',
        match: ['superior', 'superior project'],
        desc: 'Awarded in recognition of exceptional quality across all assessed dimensions — including the depth and rigour of the work undertaken, the standard of execution, and the significance of the outcomes produced. This is the highest distinction conferred by Next Idea Matters.'
    },
    outstanding: {
        key: 'outstanding',
        dots: 3,
        code: 'OD',
        name: 'Outstanding Project',
        match: ['outstanding', 'outstanding project'],
        desc: 'Awarded in recognition of high-quality work across all assessed dimensions, demonstrating clear evidence of achievement, strong execution, and outcomes of meaningful real-world relevance.'
    },
    excellent: {
        key: 'excellent',
        dots: 2,
        code: 'EX',
        name: 'Excellent Project',
        match: ['excellent', 'excellent project', 'excellence', 'excellence project'],
        desc: 'Awarded in recognition of meeting all programme requirements to a consistently high standard, with well-grounded evidence and quality of work sustained across the full scope of the project.'
    },
    honorable: {
        key: 'honorable',
        dots: 1,
        code: 'HN',
        name: 'Honorable Mention',
        match: ['honorable mention', 'honourable mention'],
        desc: 'Awarded in recognition of the successful completion of all programme requirements and a demonstrated commitment to the project throughout the programme year.'
    }
}

function normalizeAwardLabel(label) {
    return String(label || '').trim().toLowerCase().replace(/\s+/g, ' ')
}

export function resolveAwardTier(label) {
    const normalized = normalizeAwardLabel(label)
    if (!normalized) {
        return null
    }
    const tiers = Object.keys(CERTIFICATE_TIER_META)
    for (let i = 0; i < tiers.length; i++) {
        const tier = CERTIFICATE_TIER_META[tiers[i]]
        if (tier.match.indexOf(normalized) >= 0 ||
            tier.match.some(item => normalized.indexOf(item) >= 0)) {
            return tier
        }
    }
    return null
}

export function isCertificateAwardLevel(label) {
    return !!resolveAwardTier(label)
}

export function filterCertificateAwards(awards) {
    if (!awards || !awards.length) {
        return []
    }
    return awards.filter(item => isCertificateAwardLevel(item && (item.award_lable || item.award_label || item.award_name)))
}

export function getAwardDisplay(item) {
    const label = item && (item.award_lable || item.award_label || item.award_name)
    const tier = resolveAwardTier(label)
    if (tier) {
        return tier
    }
    return {
        key: 'custom',
        dots: 0,
        code: '',
        name: label || '',
        desc: ''
    }
}

/**
 * Map a weighted score to a certificate level.
 * @param {number|null|undefined} weightedScore
 * @param {object} [thresholds] - optional thresholds when algorithm is finalized
 * @returns {'Superior Project'|'Outstanding Project'|'Excellent Project'|'Honorable Mention'|null}
 *          null means no award / no certificate
 */
export function resolveCertificateLevel(weightedScore, thresholds) {
    if (weightedScore === null || weightedScore === undefined || isNaN(Number(weightedScore))) {
        return null
    }

    const score = Number(weightedScore)
    const rules = thresholds || {
        superior: 90,
        outstanding: 80,
        excellent: 70,
        honorableMention: 60
    }

    if (rules.superior != null && score >= rules.superior) {
        return 'Superior Project'
    }
    if (rules.outstanding != null && score >= rules.outstanding) {
        return 'Outstanding Project'
    }
    if (rules.excellent != null && score >= rules.excellent) {
        return 'Excellent Project'
    }
    if (rules.honorableMention != null && score >= rules.honorableMention) {
        return 'Honorable Mention'
    }
    return null
}
